import { useCallback, useRef, useState } from 'react';
import { Bars3BottomLeftIcon } from '@heroicons/react/24/outline';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useOutsideDismiss } from '../../hooks/useOutsideDismiss';
import { ChapterTabList, ChapterTools, type ChapterTabsProps } from './ChapterTabs';

/**
 * Feature 023 — the chapter tabs for viewports with no room for the rail (phones, and the
 * landscape-phone band). `study.css` shows this exactly where it hides `StudySidebar`, so the two
 * are never both on screen.
 *
 * Modelled on the reader's existing Download disclosure rather than a full-screen drawer: same
 * `aria-expanded` / `aria-controls` wiring, same Escape-to-close with focus returned to the
 * trigger. Eight chapters plus two tools do not justify a second overlay idiom.
 */
export function ChapterPicker({
  triggerClassName,
  ...tabs
}: ChapterTabsProps & { triggerClassName: string }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Choosing a chapter re-renders this same component with a new slug (the route param changes but
  // the component stays mounted), so the panel has to be dismissed explicitly.
  const dismiss = useCallback(() => setOpen(false), []);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEscapeClose(close, open);
  // Tapping the page closes the panel WITHOUT pulling focus back to the trigger — Escape is a
  // deliberate "put me back where I was", a tap elsewhere is the user moving on.
  useOutsideDismiss(rootRef, dismiss, open);

  return (
    <div ref={rootRef} className="study-phone-only relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="chapter-picker-panel"
        aria-label="Choose a chapter"
        data-testid="chapter-picker"
        className={triggerClassName}
      >
        <Bars3BottomLeftIcon className="h-5 w-5" aria-hidden="true" />
        <span className="hidden sm:inline">Chapters</span>
      </button>

      {open && (
        <div
          id="chapter-picker-panel"
          data-testid="chapter-picker-panel"
          // Right-aligned and height-capped: the trigger sits at the end of the header bar, and the
          // list must not run past a short landscape phone's viewport.
          className="absolute right-0 z-30 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-xl border border-line bg-card py-2 shadow-lg"
        >
          {/* Sentence case, not the rail's ALL-CAPS eyebrow — phones drop capitals. */}
          <h2 className="px-[22px] pb-1.5 text-xs font-semibold text-ink-soft">The notes</h2>
          <ChapterTabList {...tabs} onNavigate={dismiss} />

          <div className="mt-2 border-t border-line pt-2">
            <h2 className="px-[22px] pb-1.5 text-xs font-semibold text-ink-soft">All chapters</h2>
            <ChapterTools onNavigate={dismiss} />
          </div>
        </div>
      )}
    </div>
  );
}
